import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { AuthContextType, UserData } from "@/shared/types/auth";
import { isPortalSessionTokenValid } from "@/shared/utils/jwtAccessToken";

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<UserData | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const userData = localStorage.getItem("UserData");
    const token = localStorage.getItem("gcpToken");

    if (userData && token) {
      if (!isPortalSessionTokenValid(token)) {
        localStorage.removeItem("UserData");
        localStorage.removeItem("gcpToken");
        localStorage.removeItem("impersonating");
        setIsAuthenticated(false);
        setUser(null);
      } else {
        try {
          const parsedUser = JSON.parse(userData);
          // Only set authenticated if we have valid user data and token
          if (parsedUser && Object.keys(parsedUser).length > 0) {
            setUser(parsedUser);
            setIsAuthenticated(true);
          } else {
            // Invalid user data, clear it
            localStorage.removeItem("UserData");
            localStorage.removeItem("gcpToken");
            localStorage.removeItem("impersonating");
            setIsAuthenticated(false);
            setUser(null);
          }
        } catch (err) {
          console.error("Error parsing user data:", err);
          localStorage.removeItem("UserData");
          localStorage.removeItem("gcpToken");
          localStorage.removeItem("impersonating");
          setIsAuthenticated(false);
          setUser(null);
        }
      }
    } else {
      // No token or user data, ensure we're logged out
      setIsAuthenticated(false);
      setUser(null);
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (_email: string, _password: string): Promise<void> => {
    throw new Error("Use useAuthMutations().login instead");
  }, []);

  const logout = useCallback((): void => {
    localStorage.removeItem("UserData");
    localStorage.removeItem("gcpToken");
    localStorage.removeItem("impersonating");
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
    // Cached rows are role-redacted by the backend — none of them survive a sign-out.
    queryClient.clear();
  }, [queryClient]);

  const handleSetUser = useCallback((userData: UserData | null): void => {
    setUser(userData);
    setIsAuthenticated(userData !== null);
    if (userData) {
      const existingData = localStorage.getItem("UserData");
      if (!existingData || existingData !== JSON.stringify(userData)) {
        localStorage.setItem("UserData", JSON.stringify(userData));
      }
    } else {
      localStorage.removeItem("UserData");
      localStorage.removeItem("gcpToken");
      localStorage.removeItem("impersonating");
    }
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      error,
      login,
      logout,
      setUser: handleSetUser,
    }),
    [user, isAuthenticated, isLoading, error, login, logout, handleSetUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
